import { useState } from 'react';
import { ArrowLeft, Search, School, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue } from '@/types';

const statusColors = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  'in review': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'on hold': 'bg-orange-100 text-orange-700 border-orange-200',
  resolved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
} as const;

interface TrackIssueProps {
  onBack: () => void;
}

export function TrackIssue({ onBack }: TrackIssueProps) {
  const [issueNumber, setIssueNumber] = useState('');
  const [issue, setIssue] = useState<ReportedIssue | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!issueNumber.trim()) return;

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(name),
          grade:sms_grades(name)
        `)
        .eq('issue_number', issueNumber.trim())
        .single();

      if (error) throw error;
      setIssue(data);
    } catch (error) {
      console.error('Error searching for issue:', error);
      toast.error('Issue not found');
      setIssue(null);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen">
      <div 
        className="relative bg-[url('https://images.unsplash.com/photo-1606761568499-6d2451b23c66')] bg-cover bg-center"
        style={{ 
          backgroundImage: `
            linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
            url('https://images.unsplash.com/photo-1606761568499-6d2451b23c66')
          `
        }}
      >
        {/* Navigation */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-start">
            <Button
              variant="outline"
              onClick={onBack}
              className="bg-white hover:bg-gray-100 text-gray-900"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Report Form
            </Button>
          </div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 py-16 text-center text-white">
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Track Your Report
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Enter your issue number to check the status of your report.
          </p>
        </div>
      </div>

      {/* Track Issue Section */}
      <div className="container max-w-2xl mx-auto px-4 py-16">
        <Card className="bg-white/95 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-center">Track Your Report</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              <div className="flex gap-4">
                <Input
                  value={issueNumber}
                  onChange={(e) => setIssueNumber(e.target.value)}
                  placeholder="Enter issue number"
                  className="flex-1"
                />
                <Button onClick={handleSearch} disabled={isSearching}>
                  <Search className="h-4 w-4 mr-2" />
                  Track
                </Button>
              </div>

              {issue && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b pb-4">
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-zinc-100 rounded text-sm font-mono">
                        #{issue.issue_number}
                      </code>
                    </div>
                    <Badge
                      className={`
                        px-4 py-1.5 text-sm font-medium rounded-full border
                        ${statusColors[issue.status as keyof typeof statusColors]}
                      `}
                    >
                      {issue.status.toUpperCase()}
                    </Badge>
                  </div>

                  {issue.status === 'new' && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Your issue has been submitted and is currently under review. We will process it as soon as possible.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="bg-zinc-50 rounded-lg p-4 border">
                    <div className="flex items-start gap-3">
                      <School className="h-5 w-5 text-blue-600 mt-1" />
                      <div>
                        <p className="text-sm font-medium text-zinc-500">School</p>
                        <h3 className="text-xl font-semibold text-blue-600 mt-1">
                          {issue.school?.name}
                        </h3>
                        <p className="text-sm text-zinc-600 mt-1">
                          Grade: {issue.grade?.name}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 px-4">
                    <Clock className="h-5 w-5 text-zinc-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-zinc-500">Reported On</p>
                      <p className="text-sm text-zinc-600 mt-1">
                        {formatDate(issue.created_at!)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}